import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { MessageSquare, Send, Star, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BetaFeedbackPanel({ moduleKey }) {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState(null);

  const [thinkResponse, setThinkResponse] = useState('');
  const [improveResponse, setImproveResponse] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');

  // Check if user is a team member
  const isTeamMember = user?.team_role || false;
  const isAdmin = user?.email === 'cfenison@gmail.com' || user?.email === 'testadmin@test.com';

  useEffect(() => {
    if (!moduleKey || (!isTeamMember && !isAdmin) || !token) return;
    fetchExistingFeedback();
  }, [moduleKey, token]);

  const fetchExistingFeedback = async () => {
    try {
      const res = await fetch(`${API_URL}/api/audit/feedback/${moduleKey}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const myFeedback = data.feedback?.find(f => f.user_email === user?.email);
        if (myFeedback) {
          setExistingFeedback(myFeedback);
          setThinkResponse(myFeedback.think_response || '');
          setImproveResponse(myFeedback.improve_response || '');
          setRating(myFeedback.rating || 0);
          setComment(myFeedback.comment || '');
          setSubmitted(true);
        }
      }
    } catch {
      // Silently fail
    }
  };

  const submitFeedback = async () => {
    if (!thinkResponse.trim() && !improveResponse.trim() && !comment.trim() && rating === 0) {
      toast.error('Please provide at least one response');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/audit/feedback/${moduleKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          think_response: thinkResponse.trim(),
          improve_response: improveResponse.trim(),
          rating,
          comment: comment.trim(),
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success(existingFeedback ? 'Feedback updated!' : 'Feedback submitted!');
        fetchExistingFeedback();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit');
      }
    } catch {
      toast.error('Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  // Only show for team members
  if (!isTeamMember && !isAdmin) return null;

  return (
    <div className="mt-8 mb-4" data-testid="beta-feedback-panel">
      {/* Toggle Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="beta-feedback-toggle"
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all"
        style={{
          background: submitted ? 'rgba(16,185,129,0.06)' : 'rgba(217,119,6,0.06)',
          borderColor: submitted ? 'rgba(16,185,129,0.2)' : 'rgba(217,119,6,0.2)',
        }}
      >
        <div className="flex items-center gap-2">
          {submitted ? (
            <CheckCircle className="w-4 h-4 text-emerald-500" />
          ) : (
            <MessageSquare className="w-4 h-4 text-amber-500" />
          )}
          <span className="text-sm font-medium" style={{ color: submitted ? '#10b981' : '#d97706' }}>
            {submitted ? 'Feedback Submitted' : 'Beta Tester Feedback'}
          </span>
          {user?.team_role_label && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-stone-800 text-stone-400">
              {user.team_role_label}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
      </button>

      {/* Feedback Form */}
      {open && (
        <div className="mt-2 p-5 rounded-xl border space-y-5"
          style={{ background: 'rgba(30,30,40,0.5)', borderColor: 'rgba(120,120,140,0.15)' }}>

          {/* Q1: What does this cause you to think? */}
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold mb-2" style={{ color: '#d97706' }}>
              1. What does this lesson cause you to think?
            </label>
            <p className="text-xs text-stone-500 mb-2">One word or a short sentence — your first impression.</p>
            <input
              data-testid="feedback-think-input"
              type="text"
              value={thinkResponse}
              onChange={e => setThinkResponse(e.target.value)}
              placeholder="e.g. Clarity, Conviction, Questioned my assumptions..."
              className="w-full bg-stone-900/60 border border-stone-700 rounded-lg px-3 py-2.5 text-sm text-stone-200 placeholder-stone-600 focus:border-amber-600 focus:outline-none"
              disabled={loading}
            />
          </div>

          {/* Q2: What could be resaid? */}
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold mb-2" style={{ color: '#d97706' }}>
              2. What could be resaid to improve the thought logic process?
            </label>
            <p className="text-xs text-stone-500 mb-2">Suggest how the lesson could communicate more effectively.</p>
            <textarea
              data-testid="feedback-improve-input"
              value={improveResponse}
              onChange={e => setImproveResponse(e.target.value)}
              placeholder="e.g. The connection between verse 3 and 4 could be clearer if..."
              rows={3}
              className="w-full bg-stone-900/60 border border-stone-700 rounded-lg px-3 py-2.5 text-sm text-stone-200 placeholder-stone-600 focus:border-amber-600 focus:outline-none resize-none"
              disabled={loading}
            />
          </div>

          {/* Rating */}
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold mb-2 text-stone-400">
              Lesson Effectiveness (optional)
            </label>
            <div className="flex items-center gap-1" data-testid="feedback-rating">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-0.5 transition-transform hover:scale-110"
                  disabled={loading}
                >
                  <Star
                    className={`w-5 h-5 transition-colors ${
                      n <= (hoverRating || rating)
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-stone-700'
                    }`}
                  />
                </button>
              ))}
              {rating > 0 && <span className="text-xs text-stone-500 ml-2">{rating}/5</span>}
            </div>
          </div>

          {/* Additional Comment */}
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold mb-2 text-stone-400">
              Additional Notes (optional)
            </label>
            <textarea
              data-testid="feedback-comment-input"
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Any other thoughts, questions, or suggestions..."
              rows={2}
              className="w-full bg-stone-900/60 border border-stone-700 rounded-lg px-3 py-2.5 text-sm text-stone-200 placeholder-stone-600 focus:border-amber-600 focus:outline-none resize-none"
              disabled={loading}
            />
          </div>

          {/* Submit */}
          <button
            data-testid="feedback-submit-btn"
            onClick={submitFeedback}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
            style={{ background: '#d97706', color: '#fff' }}
          >
            <Send className="w-4 h-4" />
            {loading ? 'Submitting...' : submitted ? 'Update Feedback' : 'Submit Feedback'}
          </button>
        </div>
      )}
    </div>
  );
}
